import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {BleedUnit, getDefaultValForUnit} from '@PosterWhiteboard/poster/bleed.class';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {PageBleedUnitSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-bleed-unit-size-option';
import {useAppSelector} from '@/hooks';

export function PageBleedOption(): ReactElement<any> | null {
  const bleedUnits = useAppSelector((state) => {
    return state.posterEditor.posterObject?.bleed.unit;
  });
  const isBleedEnabled = useAppSelector((state) => {
    return state.posterEditor.posterObject?.bleed.enabled;
  });

  if (isBleedEnabled === undefined || bleedUnits === undefined) {
    return null;
  }

  const getDropdownBleedDropdownItemsText = (item: BleedUnit): string => {
    switch (item) {
      case BleedUnit.INCHES:
        return window.i18next.t('pmwjs_bleed_inch');
      case BleedUnit.MILLI_METER:
        return window.i18next.t('pmwjs_bleed_millimeter');
      default:
        throw new Error('Unhandled bleed dropdown item');
    }
  };

  const getBleedUnitDropdown = (): ReactElement<any> => {
    return (
      <PosterEditingBaseDropdownOption
        id="bleed-unit-dropdown-option"
        position={DROPDOWN_POSITION.FIXED}
        label={window.i18next.t('pmwjs_units')}
        onClick={(id: BleedUnit): void => {
          void window.posterEditor?.whiteboard?.updateFromObject(
            {
              bleed: {
                unit: id,
                val: getDefaultValForUnit(id),
              },
            },
            {
              undoable: false,
            }
          );
        }}
        dropdownItems={[
          {
            text: getDropdownBleedDropdownItemsText(BleedUnit.INCHES),
            id: BleedUnit.INCHES,
          },
          {
            text: getDropdownBleedDropdownItemsText(BleedUnit.MILLI_METER),
            id: BleedUnit.MILLI_METER,
          },
        ]}
        selectedItemId={bleedUnits}
        leftOffset={12}
        isForSubOption
      />
    );
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (isBleedEnabled) {
      return (
        <>
          {getBleedUnitDropdown()}
          <PageBleedUnitSizeOption />
        </>
      );
    }
    return null;
  };

  return (
    <ContextualMenuToggleOption
      id="page-bleed-option"
      label={window.i18next.t('pmwjs_bleed')}
      isChecked={isBleedEnabled}
      onClick={(): void => {
        void window.posterEditor?.whiteboard?.updateFromObject(
          {
            bleed: {
              enabled: !isBleedEnabled,
            },
          },
          {
            undoable: false,
          }
        );
      }}
    >
      {getSubOptions()}
    </ContextualMenuToggleOption>
  );
}
